import { FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { AxiosError } from 'axios';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useAccountAPI } from '../../../../api/use-account-api';
import { useData } from '../../../../contexts/data-context/use-data';
import { LoadingSection } from '../../../../design-system/loading/loading';
import { handleException } from '../../../../utils/handle-exception';
import { postSwrFetcherWithDynamicUrl } from '../../../../utils/post-swr-fetcher-with-dynamic-url';
import { ConnectedAccountsPage } from './connected-accounts-page';

type UnlinkAccountArg = {
  urlSuffix: string;
};

export const ConnectedAccountsPageWrapper = () => {
  const { syncAccounts, isSyncing: isSyncingAccounts } = useAccountAPI();
  const navigate = useNavigate();
  const { data, isLoading, error, mutations } = useData();
  const { showBoundary } = useErrorBoundary();
  const { gmailConnectorPoc, isDemoAccountsEnabled } = useFlags();

  const { trigger: triggerUnlinkAccount } = useSWRMutation<void, AxiosError, string, UnlinkAccountArg>(
    '/api/accounts/',
    postSwrFetcherWithDynamicUrl,
  );

  if (isLoading) {
    return <LoadingSection text="Loading accounts..." />;
  }

  if (!data) {
    showBoundary(error);

    return null;
  }

  const { accounts } = data;

  const onSyncAccounts = () => {
    // Using data from first company because orgs can only have one company for now
    syncAccounts(accounts[0].company.id);
  };

  if (accounts.length === 0) {
    navigate('../open-seapoint-account');

    return null;
  }

  const onDeleteAccount = async (accountId: string) => {
    try {
      await triggerUnlinkAccount({ urlSuffix: `${accountId}/unlink` });

      await mutations.handleDeleteUpdates({
        entityType: 'account',
        entityIds: [accountId],
      });

      toast.success('The account has been disconnected');
    } catch (error) {
      handleException({ error, toastMessage: 'Failed to disconnect the account' });
    }
  };

  const openBankingAccounts = accounts.filter(
    (account) =>
      account.fspAdapter === FinancialServiceProviderAdapters.GOCARDLESS ||
      account.fspAdapter === FinancialServiceProviderAdapters.YAPILY,
  );
  const modulrAccounts = accounts.filter((account) => account.fspAdapter === FinancialServiceProviderAdapters.MODULR);
  const demoAccounts = accounts.filter(
    (account) => account.fspAdapter === FinancialServiceProviderAdapters.GOCARDLESS_DEMO_DATA,
  );

  // Determine which demo variants already exist
  const hasDemoEUR = demoAccounts.some((account) => account.currency === 'EUR');
  const hasDemoGBP = demoAccounts.some((account) => account.currency === 'GBP');

  return (
    <ConnectedAccountsPage
      shouldShowConnectDemoDataEURButton={isDemoAccountsEnabled && !hasDemoEUR}
      shouldShowConnectDemoDataGBPButton={isDemoAccountsEnabled && !hasDemoGBP}
      shouldShowGmailConnectorButton={gmailConnectorPoc}
      openBankingAccounts={openBankingAccounts}
      modulrAccounts={modulrAccounts}
      demoAccounts={demoAccounts}
      onGenerateDemoDataEUR={() => {
        // EUR via Revolut
        const demoInstitutionData = {
          name: 'Revolut',
          logo: 'https://storage.googleapis.com/gc-prd-institution_icons-production/UK/PNG/revolut.png',
        };
        localStorage.setItem('selectedInstitution', JSON.stringify(demoInstitutionData));
        window.location.href = `/api/gocardless/auth/${accounts[0].company.id}?ref=demo&demo=true&demoVariant=eur_revolut`;
      }}
      onGenerateDemoDataGBP={() => {
        // GBP via HSBC
        const demoInstitutionData = {
          name: 'HSBC UK',
          logo: 'https://storage.googleapis.com/gc-prd-institution_icons-production/UK/PNG/hsbc.png',
        };
        localStorage.setItem('selectedInstitution', JSON.stringify(demoInstitutionData));
        window.location.href = `/api/gocardless/auth/${accounts[0].company.id}?ref=demo&demo=true&demoVariant=gbp_hsbc`;
      }}
      isSyncingAccounts={isSyncingAccounts}
      onSyncAccounts={onSyncAccounts}
      onLinkAccount={() => {
        navigate('../connect-open-banking');
      }}
      onLinkEmailAccount={() => {
        navigate('../connect-email');
      }}
      onDeleteAccount={onDeleteAccount}
    />
  );
};
